import type { Ref } from "vue";
import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { RouteRecordRaw } from "vue-router";
import router from "@/router";
import { ElMessage } from "element-plus";
import { adminMenuTree, flattenAdminRoutes } from "@/router/modules/admin";
import { getAdminAsyncRoutesApi, type AsyncRouteItem } from "@/network/admin/NetworkMenu";
import { getJslsUserInfoApi, normalizeJslsAuth, normalizeJslsPlatformCodes, type JslsUserData } from "@/network/admin/common";
import type { JslsPlatformCode } from "@/network/admin/instant-retail/NetworkProduct";
import { useAdminTagsViewStore } from "@/stores/modules/admin/tagsView";
import { clearProductDraft } from "@/views/admin/instant-retail/product/productDraft";

// 动态匹配 views 下所有 vue 组件
const modules = import.meta.glob("@/views/**/*.vue");

/**
 * 将后端返回的动态菜单数据递归转换为 Vue Router RouteRecordRaw
 */
export function filterAsyncRoutes(routes: AsyncRouteItem[]): RouteRecordRaw[] {
    const res: RouteRecordRaw[] = [];

    routes.forEach((item) => {
        const route: any = {
            path: item.path,
            name: item.name,
            redirect: item.redirect,
            meta: item.meta || {}
        };

        if (item.component) {
            // 支持传入 views/admin/goods/list 格式
            const componentPath = item.component.startsWith("/") ? item.component : `/${item.component}`;
            const key = `/src/views${componentPath}.vue`;
            if (modules[key]) {
                route.component = modules[key];
            } else {
                console.warn(`[Permission] 未找到对应的组件路径: ${key}`);
            }
        }

        if (item.children && item.children.length > 0) {
            route.children = filterAsyncRoutes(item.children);
        }

        res.push(route as RouteRecordRaw);
    });

    return res;
}

/**
 * 根据用户权限数据递归过滤菜单树
 * - meta.authKey 为 string（如 "XCX"）：检查 platforms 是否包含该值
 * - meta.authKey 为 number[]（如 [4]、[7]、[9]）：检查 jslsAuth 是否包含其中任意值
 * - 无 authKey：不受限制，始终保留
 */
function filterMenuByAuth(
    routes: RouteRecordRaw[],
    jslsAuth: number[],
    platforms: string[]
): RouteRecordRaw[] {
    const result: RouteRecordRaw[] = [];

    routes.forEach((route) => {
        const authKey = (route.meta as any)?.authKey;

        // 权限检查
        if (authKey !== undefined) {
            if (typeof authKey === "string") {
                // platforms 权限（如 "XCX"）
                if (!platforms.includes(authKey)) {
                    return; // 跳过无权限节点
                }
            } else if (Array.isArray(authKey)) {
                // jslsAuth 权限（如 [4]、[7]、[9]）
                const hasAuth = (authKey as number[]).some((code) => jslsAuth.includes(code));
                if (!hasAuth) {
                    return; // 跳过无权限节点
                }
            }
        }

        // 深拷贝当前节点，递归过滤子节点
        const cloned: RouteRecordRaw = { ...route };
        if (route.children && route.children.length > 0) {
            cloned.children = filterMenuByAuth(route.children, jslsAuth, platforms);
        }

        result.push(cloned);
    });

    return result;
}

/**
 * 校验指定路由记录在当前已加载的权限列表中是否具备访问权限
 */
export function hasRoutePermission(route: { name?: any; path: string }, allowedRoutes: RouteRecordRaw[]): boolean {
    if (route.path === "/pcadmin/dashboard" || route.name === "AdminDashboard") {
        return true;
    }
    return allowedRoutes.some((r) => r.name === route.name || r.path === route.path);
}

export interface AdminPermissionState {
    routes: Ref<RouteRecordRaw[]>;
    addRoutes: Ref<RouteRecordRaw[]>;
    isRoutesLoaded: Ref<boolean>;
    storeVersion: Ref<number>;
    jslsUserData: Ref<JslsUserData | null>;
    settledPlatforms: Ref<JslsPlatformCode[]>;
    jslsAuth: Ref<number[]>;
    setJslsUserData: (data: JslsUserData | null) => void;
    refreshJslsUser: () => Promise<JslsPlatformCode[]>;
    generateRoutes: () => Promise<RouteRecordRaw[]>;
    handleStoreChange: (options?: { targetStoreId?: number | string; source?: "header" | "outlet" }) => Promise<void>;
    resetRoutes: () => void;
}

export const useAdminPermissionStore = defineStore("adminPermission", (): AdminPermissionState => {
    // 菜单展示用的完整层级路由树
    const routes = ref<RouteRecordRaw[]>([]);
    // 动态添加到 router 的扁平路由列表
    const addRoutes = ref<RouteRecordRaw[]>([]);
    // 是否已经完成动态路由挂载
    const isRoutesLoaded = ref<boolean>(false);
    // 门店变更响应式版本标记（供外部联动组件感知）
    const storeVersion = ref<number>(0);
    // 即时零售 PC 用户信息：platforms 为已入驻平台，jslsAuth 为权限编号
    const jslsUserData = ref<JslsUserData | null>(null);
    const settledPlatforms = computed(() => normalizeJslsPlatformCodes(jslsUserData.value?.platforms));
    const jslsAuth = computed(() => normalizeJslsAuth(jslsUserData.value?.jslsAuth));

    const setJslsUserData = (data: JslsUserData | null) => {
        if (!data) {
            jslsUserData.value = null;
            return;
        }
        jslsUserData.value = {
            ...data,
            // data.platforms
            platforms: normalizeJslsPlatformCodes(data.platforms),
            jslsAuth: normalizeJslsAuth(data.jslsAuth)
        };
    };

    /** 拉取 /v4/users/jsls/pc，写入 platforms 与 jslsAuth */
    const refreshJslsUser = async (): Promise<JslsPlatformCode[]> => {
        const res = await getJslsUserInfoApi();
        setJslsUserData(res?.data ?? null);
        return settledPlatforms.value;
    };

    /**
     * 生成后台合并路由（静态 + 动态），并根据用户权限过滤菜单
     */
    const generateRoutes = async (): Promise<RouteRecordRaw[]> => {
        try {
            // 1. 并行获取：后端动态菜单 + 用户权限数据
            const [menuRes, jslsRes] = await Promise.all([
                getAdminAsyncRoutesApi().catch(() => null),
                getJslsUserInfoApi().catch(() => null)
            ]);

            // 2. 写入已入驻平台与权限，菜单过滤走同一份数据
            setJslsUserData(jslsRes?.data || null);

            // 3. 基于权限过滤静态菜单树
            const baseAdminMenu = adminMenuTree[0] || ({} as RouteRecordRaw);
            const filteredStaticChildren = filterMenuByAuth(
                baseAdminMenu.children || [],
                jslsAuth.value,
                settledPlatforms.value
            );

            // 4. 处理后端动态菜单数据
            const asyncData = menuRes?.data || [];
            const accessedRoutes = filterAsyncRoutes(asyncData);

            // 5. 合并层级树用于 Sidebar 菜单展示
            const fullMenuChildren = [
                ...filteredStaticChildren,
                ...accessedRoutes
            ];

            routes.value = [{
                ...baseAdminMenu,
                children: fullMenuChildren
            }];

            // 6. 提取扁平路由列表用于 router.addRoute（排除 Dashboard，已在静态路由中注册）
            const dynamicStaticChildren = filteredStaticChildren.filter(
                (r) => r.name !== "AdminDashboard"
            );
            const flatStaticRoutes = flattenAdminRoutes(dynamicStaticChildren, "/pcadmin");
            const flatAsyncRoutes = flattenAdminRoutes(accessedRoutes, "/pcadmin");
            const allFlatRoutes = [...flatStaticRoutes, ...flatAsyncRoutes];

            addRoutes.value = allFlatRoutes;
            isRoutesLoaded.value = true;

            return allFlatRoutes;
        } catch (error) {
            console.error("[Permission] generateRoutes error:", error);
            routes.value = [];
            addRoutes.value = [];
            isRoutesLoaded.value = true;
            return [];
        }
    };

    /**
     * 响应门店切换，主动更新后台权限、侧边栏菜单、路由与页面状态
     */
    const handleStoreChange = async (options?: {
        targetStoreId?: number | string;
        source?: "header" | "outlet";
    }): Promise<void> => {
        if (options?.targetStoreId !== undefined && options?.targetStoreId !== null) {
            localStorage.setItem("storeId", String(options.targetStoreId));
        }
        // 建品草稿只属于切店前的门店，避免新店进创建页时弹出「上次未保存的内容」
        clearProductDraft();

        try {
            // 1. 主动重新生成后台合并路由与侧边栏菜单树
            const asyncRoutes = await generateRoutes();

            // 2. 将最新的动态子路由重新注册到 AdminLayout
            asyncRoutes.forEach((route) => {
                router.addRoute("AdminLayout", route);
            });

            // 3. 安全清理 TagsView 中属于已失效权限的非固定标签
            const tagsViewStore = useAdminTagsViewStore();
            const allowedPaths = new Set(addRoutes.value.map((r) => r.path));
            allowedPaths.add("/pcadmin/dashboard");
            const allowedNames = new Set(addRoutes.value.map((r) => r.name as string).filter(Boolean));
            allowedNames.add("AdminDashboard");

            const viewsToRemove = tagsViewStore.visitedViews.filter((view) => {
                if (view.affix) return false;
                const pathAllowed = allowedPaths.has(view.path);
                const nameAllowed = view.name ? allowedNames.has(view.name) : false;
                return !pathAllowed && !nameAllowed;
            });

            for (const view of viewsToRemove) {
                await tagsViewStore.delView(view);
            }

            // 4. 触发全局联动版本自增（通知侧边栏选择器等组件同步更新实体店与徽章）
            storeVersion.value++;

            // 5. 校验当前所在页面的权限合法性
            const currentRoute = router.currentRoute.value;
            const isAdminRoute = currentRoute.path.startsWith("/pcadmin");

            if (isAdminRoute) {
                const isCurrentPermitted = hasRoutePermission(
                    { name: currentRoute.name, path: currentRoute.path },
                    addRoutes.value
                );

                if (!isCurrentPermitted) {
                    ElMessage.warning("当前门店未绑定自营商城或无此功能权限，已跳转至首页");
                    await router.replace("/pcadmin/dashboard");
                } else {
                    // 若当前路由依然具备权限，刷新当前视图组件以加载新门店数据
                    await router.replace({ path: "/refresh" });
                }
            }
        } catch (error) {
            console.error("[Permission] handleStoreChange error:", error);
            ElMessage.error("更新门店权限失败，请重试");
        }
    };

    /**
     * 重置路由状态（退出登录时调用）
     */
    const resetRoutes = () => {
        routes.value = [];
        addRoutes.value = [];
        isRoutesLoaded.value = false;
        jslsUserData.value = null;
        storeVersion.value++;
        clearProductDraft();
    };

    return {
        routes,
        addRoutes,
        isRoutesLoaded,
        storeVersion,
        jslsUserData,
        settledPlatforms,
        jslsAuth,
        setJslsUserData,
        refreshJslsUser,
        generateRoutes,
        handleStoreChange,
        resetRoutes
    };
}, {
    persist: {
        paths: ["jslsUserData"],
        afterRestore: (ctx) => {
            ctx.store.isRoutesLoaded = false;
            ctx.store.routes = [];
            ctx.store.addRoutes = [];
        }
    }
});
